<script setup lang="ts">
import { computed } from "vue";

const isToggled = defineModel<boolean>("isToggled");
const { disabled = false, disabledState = false } = defineProps<{
  disabled?: boolean;
  disabledState?: boolean; // The state of the toggle if it is disabled
}>();

const toggle = () => {
  if (!disabled) {
    isToggled.value = !isToggled.value;
  }
};

const handleKeydown = (event: KeyboardEvent) => {
  if (disabled) return;

  if (event.key === "Enter" || event.key === " ") {
    event.preventDefault();
    toggle();
  }
};

const currentState = computed(() => (disabled ? disabledState : isToggled.value));
</script>

<template>
  <div
    class="slide-toggle"
    :class="{ toggled: currentState, disabled: disabled }"
    :tabindex="disabled ? -1 : 0"
    role="switch"
    :aria-checked="currentState"
    :aria-disabled="disabled"
    @click="toggle"
    @keydown="handleKeydown"
  >
    <div class="thumb"></div>
  </div>
</template>

<style scoped>
.slide-toggle {
  --slide-toggle-height: var(--numbers-16);

  position: relative;
  display: inline-flex;
  width: calc(var(--slide-toggle-height) * 2);
  height: var(--slide-toggle-height);
  border-radius: var(--slide-toggle-height);
  background-color: var(--background-brand);
  cursor: pointer;
  outline: none;
  transition:
    filter var(--animation-duration) var(--animation-easing),
    opacity var(--animation-duration) var(--animation-easing),
    background-color var(--animation-duration) var(--animation-easing);

  .thumb {
    --thumb-size: calc(var(--slide-toggle-height) - 2px);

    position: absolute;
    top: 1px;
    left: 1px;
    width: var(--thumb-size);
    height: var(--thumb-size);
    border-radius: var(--thumb-size);
    background-color: var(--background-primary);
    transition: left var(--animation-duration) var(--animation-easing);
  }

  &.toggled {
    background-color: var(--background-brand);

    .thumb {
      left: calc((var(--slide-toggle-height) * 2) - 1px - var(--thumb-size));
    }
  }

  &.disabled {
    filter: grayscale(1);
    opacity: 0.5;
    pointer-events: none;
  }

  &:active:not(:disabled) {
    box-shadow: 0 0 0 3px rgb(196 202 255 / 51%);
  }
}
</style>
